import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { isEmpty } from 'lodash'
import { useHistory } from 'react-router-dom'
import ClientHighlightedList from '../../ClientCardsViewV2/ClientCardView/ClientHighlightedList'
import { useDates } from '../../../../hooks/useDates'
import { useSearchClients } from '../../../../api/clients'
import ClientCardItem from '../../ClientCardsViewV2/ClientCardView/ClientCard/ClientCardItem'
import useFetchClientBalances from '../../ClientCardsViewV2/hooks/useClientBalances'
import { mapClientBalancesToKeyValue } from '../../ClientCardsViewV2/helpers'
import { CALC_TYPES, ICON_NAMES } from '../../../../constants'
import { useRelativeDateRange } from '../../../molecules/RelativeDateSelect'
import { useAdvisorHome } from '../AdvisorHomeContext'
import { useSetCurrentClient } from '../../../../redux/slices/appContext'
import AdvisorHomeEmptySection from '../AdvisorHomeEmptySection'
import { useGetUserWheelViewPath } from '../../ClientCardsViewV2/hooks/useGetUserWheelViewPath'
import TopClientFooter from './TopClientFooter'

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    gap: '1rem',
    overflowX: 'auto',
    width: '100%',
    marginTop: '0.5rem',
    paddingBottom: '0.5rem'
  },
  actions: { display: 'flex', alignItems: 'center', '& > hr': { width: '2px', margin: '0 0.25rem', minHeight: '2rem' } },
  skeletonsContainer: { display: 'flex', gap: '1rem', flexWrap: 'nowrap' }
}))

const TopClients = ({
  limit,
  defaultDateRange,
  emptyStateLabel,

  /** Demo/footer overrides applied by sorted client index (modulo length). Pass from Abundance Engine view JSX or Storybook — omit in production until API supplies fields. */
  footerMockSequence,

  // NEW: allows AH_TopClients to style nested TopClients elements
  componentStyles
}) => {
  const classes = useStyles()
  const history = useHistory()
  const setCurrentClient = useSetCurrentClient()
  const { scope } = useAdvisorHome()
  const { availableDates } = useDates()
  const { dateRange } = useRelativeDateRange(defaultDateRange, availableDates)

  const clientBalancesQuery = useMemo(() => {
    const scopeFilters = {}
    if (scope) scopeFilters.advisorIds = scope.values
    return {
      query: { take: limit, startDate: dateRange?.startDate, endDate: dateRange?.endDate, calcType: CALC_TYPES.balance, orderBy: 'endingValue DESC', ...scopeFilters },
      options: { mapper: mapClientBalancesToKeyValue, enabled: Boolean(dateRange) }
    }
  }, [dateRange, limit, scope])

  const { path: redirectPath, isLoading: isLoadingRedirectPath } = useGetUserWheelViewPath()
  const { data: clientBalances = {}, isLoading: isLoadingClientBalances } = useFetchClientBalances(clientBalancesQuery.query, clientBalancesQuery.options)

  const clientsQuery = useMemo(() => {
    const clientIds = Object.keys(clientBalances)
    return {
      query: {
        filters: { clientId: [{ op: 'in', value: clientIds }], ...(!isEmpty(scope?.values) ? { groupIds: [{ op: 'in', value: scope?.values }] } : {}) },
        includes: { avatars: true }
      },
      queryOptions: { enabled: !isLoadingClientBalances, mapper: (data) => data?.data || [] }
    }
  }, [scope, clientBalances, isLoadingClientBalances])

  const { data = [], isLoading } = useSearchClients(clientsQuery.query, clientsQuery.queryOptions)

  const clients = useMemo(() => {
    const sorted = data
      .map((client) => ({ ...client, balance: clientBalances?.[client?.clientId] || {} }))
      .sort((a, b) => a.balance.endingValue > b.balance.endingValue ? -1 : 1)

    if (!footerMockSequence?.length) {
      return sorted
    }

    return sorted.map((client, idx) => {
      const mock = footerMockSequence[idx % footerMockSequence.length]
      if (!mock) return client
      const next = { ...client }
      if (mock.taskCount !== undefined) next.taskCount = mock.taskCount
      if (mock.nextMeetingDate !== undefined) next.nextMeetingDate = mock.nextMeetingDate
      return next
    })
  }, [clientBalances, data, footerMockSequence])

  const onClickClient = useCallback((client) => () => {
    setCurrentClient(client.clientId)
    history.push(redirectPath)
  }, [history, redirectPath, setCurrentClient])

  if (isEmpty(clients) && !isLoading) {
    return <AdvisorHomeEmptySection icon={ICON_NAMES.paperGlassMagnifier} title='No data available' description={emptyStateLabel} borderedContent />
  }

  return (
    <div
      className={classes.content}
      // NEW: styles the outer TopClients scrolling content container
      style={componentStyles?.content}
    >
      <ClientHighlightedList
        clients={clients}
        isLoading={isLoading || isLoadingRedirectPath}
        skeletonContainerClassName={classes.skeletonsContainer}
        // NEW: pass list-level style slots down
        componentStyles={componentStyles?.list}
        cardComponent={({ loading, ...clientProps }) => (
          <ClientCardItem
            client={clientProps}
            noPadding
            disableHover
            tooltipTitle='Go to client'
            loading={isLoading}
            onClick={onClickClient({ clientId: clientProps?.clientId })}
            autoWidth
            contentAlign='left'
            flatten
            showDivider={false}
            avatarAlign='center'
            // NEW: pass card-level style slots down
            componentStyles={componentStyles?.card}
            footer={(
              <div style={componentStyles?.footer}>
                <TopClientFooter
                  balance={clientProps.balance}
                  taskCount={clientProps.taskCount}
                  nextMeetingDate={clientProps.nextMeetingDate}
                />
              </div>
            )}
          />
        )}
      />
    </div>
  )
}

TopClients.propTypes = {
  limit: PropTypes.number,
  defaultDateRange: PropTypes.string,
  emptyStateLabel: PropTypes.string,
  footerMockSequence: PropTypes.arrayOf(
    PropTypes.shape({
      taskCount: PropTypes.number,
      nextMeetingDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
    })
  ),

  // NEW: nested style slots for TopClients and children
  componentStyles: PropTypes.shape({
    content: PropTypes.object,
    footer: PropTypes.object,
    list: PropTypes.shape({
      item: PropTypes.object,
      emptyTitle: PropTypes.object
    }),
    card: PropTypes.shape({
      container: PropTypes.object,
      content: PropTypes.object,
      bookmark: PropTypes.object,
      avatarWrap: PropTypes.object,
      titleContainer: PropTypes.object,
      title: PropTypes.object,
      divider: PropTypes.object
    })
  })
}

TopClients.defaultProps = {
  limit: 5,
  title: 'Top Clients',
  defaultDateRange: 'L30D',
  redirectTo: undefined,
  emptyStateLabel: '',
  footerMockSequence: undefined,

  // NEW
  componentStyles: undefined
}

export default TopClients
